"use client"

import { useState, useEffect } from "react"
import {
  Layout,
  Menu,
  Avatar,
  Button,
  message,
  Modal,
  Space,
  Spin,
  Drawer,
  Grid
} from "antd"
import {
  UserOutlined,
  ShoppingOutlined,
  WalletOutlined,
  LogoutOutlined,
  HomeOutlined,
  CarryOutOutlined,
  BankOutlined,
  ControlOutlined,
  MenuOutlined,
  LeftOutlined,
  CreditCardOutlined
} from "@ant-design/icons"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useWallet } from "../../contexts/WalletContext"
import { useCurrency } from "../../contexts/CurrencyContext"
import "../../pages/AccountPage.css"

const { Sider, Content, Header } = Layout
const { confirm } = Modal
const { useBreakpoint } = Grid

const CustomerLayout = ({ children }) => {
  const { formatMoney } = useCurrency()
  const screens = useBreakpoint()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated, loading: authLoading, checkAuth } = useAuth()
  const { balance, getWallet } = useWallet()
  const [collapsed, setCollapsed] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loading, setLoading] = useState(true)

  // Determine if we should show sidebar (desktop) or drawer (mobile)
  const showSidebar = screens.lg && !collapsed
  const showDrawer = !screens.lg
  const sidebarWidth = showSidebar ? (collapsed ? 80 : 200) : 0

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated && !checkAuth()) {
        navigate('/login')
        return
      }
      setLoading(false)
    }
  }, [isAuthenticated, authLoading])

  useEffect(() => {
    if (isAuthenticated && balance === null) {
      getWallet()
    }
  }, [isAuthenticated, balance, getWallet])

  // Auto-collapse on smaller screens
  useEffect(() => {
    if (!screens.lg) {
      setCollapsed(false) // Ensure not collapsed on mobile since we use drawer
    }
  }, [screens.lg])

  const getCurrentMenuKey = () => {
    const path = location.pathname
    if (path === "/account/dashboard") return "dashboard"
    if (path === "/account/orders") return "orders"
    if (path === "/account/transactions") return "transactions"
    if (path === "/account/payments") return "payments"
    if (path === "/account/details") return "account"
    return ""
  }

  const menuItems = [
    {
      key: "dashboard",
      icon: <UserOutlined />,
      label: "Dashboard",
      onClick: () => {
        navigate("/account/dashboard")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "orders",
      icon: <CarryOutOutlined />,
      label: "Orders",
      onClick: () => {
        navigate("/account/orders")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "transactions",
      icon: <BankOutlined />,
      label: "Transactions",
      onClick: () => {
        navigate("/account/transactions")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "payments",
      icon: <CreditCardOutlined />,
      label: "Payments",
      onClick: () => {
        navigate("/account/payments")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "account",
      icon: <ControlOutlined />,
      label: "Account Settings",
      onClick: () => {
        navigate("/account/details")
        if (showDrawer) setDrawerVisible(false)
      }
    },
  ]

  const handleLogout = () => {
    confirm({
      title: 'Confirm Logout',
      content: 'Are you sure you want to sign out?',
      onOk() {
        logout()
        message.success('Logged out successfully')
        navigate('/')
      },
    })
  }

  const SidebarContent = () => (
    <>
      <div style={{
        padding: collapsed && !showDrawer ? '16px 8px' : '16px',
        textAlign: 'center',
        borderBottom: '1px solid #d9d9d9'
      }}>
        <Avatar
          size={collapsed && !showDrawer ? 32 : 64}
          icon={<UserOutlined />}
          style={{ marginBottom: '8px' }}
        >
          {(!collapsed || showDrawer) && user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        {(!collapsed || showDrawer) && (
          <div style={{ color: '#000000', fontSize: screens.xs ? '12px' : '14px', fontWeight: 'bold' }}>
            {user?.name}
          </div>
        )}
        {showDrawer && (
          <div style={{ color: '#1890ff', fontSize: '12px', marginTop: '4px' }}>
            Customer Portal
          </div>
        )}
      </div>

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[getCurrentMenuKey()]}
        items={menuItems}
        style={{
          border: 'none',
          fontSize: screens.xs ? '14px' : '16px',
          background: 'transparent'
        }}
        onClick={() => {
          if (showDrawer) setDrawerVisible(false)
        }}
      />

      <div style={{
        position: 'absolute',
        bottom: 16,
        left: collapsed && !showDrawer ? 8 : 16,
        right: collapsed && !showDrawer ? 8 : 16
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => {
              navigate("/")
              if (showDrawer) setDrawerVisible(false)
            }}
            style={{
              width: '100%',
              color: '#000000',
              textAlign: 'left',
              padding: collapsed && !showDrawer ? '4px' : '8px 16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: 'transparent',
              transition: 'all 0.2s'
            }}
          >
            {(!collapsed || showDrawer) && 'Shop'}
          </Button>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              width: '100%',
              color: '#000000',
              textAlign: 'left',
              padding: collapsed && !showDrawer ? '4px' : '8px 16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: 'transparent',
              transition: 'all 0.2s'
            }}
          >
            {(!collapsed || showDrawer) && 'Logout'}
          </Button>
        </div>
      </div>
    </>
  )

  if (loading || authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return null // Will redirect in useEffect
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {showSidebar && (
        <Sider
          collapsible={!showDrawer}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="light"
          width={200}
          style={{
            background: '#fafafa',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            overflow: 'auto',
            zIndex: 100,
            border: 'none',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)'
          }}
          trigger={null}
        >
          <SidebarContent />
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={null}
        placement="left"
        closable={false}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        styles={{ body: { padding: 0, background: '#fafafa' } }}
        style={{ zIndex: 1000 }}
        width={280}
      >
        <SidebarContent />
      </Drawer>

      <Layout style={{
        marginLeft: showSidebar ? sidebarWidth : 0,
        transition: 'margin-left 0.2s'
      }}>
        <Header style={{
          background: '#fff',
          padding: screens.xs ? '0 16px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          height: screens.xs ? '56px' : '64px'
        }}>
          <Space>
            {showDrawer && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: '18px' }}
              />
            )}
            {user?.role === 'admin' && (
              <Button
                type="primary"
                onClick={() => navigate('/admin/dashboard')}
                size={screens.xs ? 'small' : 'default'}
              >
                Admin Panel
              </Button>
            )}
          </Space>

          <Space size={screens.xs ? 'small' : 'default'}>
            <Space size={4}>
              <Avatar size={screens.xs ? 24 : 32} icon={<WalletOutlined />} />
              <span style={{
                fontWeight: 'bold',
                fontSize: screens.xs ? '12px' : '14px'
              }}>
                {formatMoney(balance ?? 0)}
              </span>
            </Space>
          </Space>
        </Header>

        <Content style={{
          margin: screens.xs ? '8px' : '24px',
          background: '#fff',
          padding: screens.xs ? '16px' : '24px',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 80px)'
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default CustomerLayout
